import {
  GameState,
  GameConfig,
  PlayerState,
  MatchResult,
  GameEvent,
  DEFAULT_GAME_CONFIG,
  DEFAULT_BENCH_LIMIT,
} from '../types';
import { CardPool } from './CardPool';
import { DeckPhase } from './DeckPhase';
import { MatchPhase, determineFirstPlayer } from './MatchPhase';
import { BotAI, createBotPlayer } from './BotAI';
import { TournamentManager, MatchPairing } from './TournamentManager';

function generateId(): string {
  return Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
}

// 準備完了状態
interface ReadyState {
  playerId: string;
  isReady: boolean;
  timestamp: number;
}

export class GameManager {
  private state: GameState;
  private cardPool: CardPool;
  private tournament: TournamentManager;
  private botAIs: Map<string, BotAI> = new Map();
  private eventListeners: ((event: GameEvent) => void)[] = [];
  
  // 準備完了管理
  private readyStates: Map<string, ReadyState> = new Map();
  private readyTimeout: number | null = null;
  private readonly READY_TIMEOUT_MS = 300000; // 5分タイムアウト (マルチプレイでも十分な時間)

  constructor(config: Partial<GameConfig> = {}) {
    this.cardPool = new CardPool();
    this.tournament = new TournamentManager({
      qualifyingRounds: config.qualifyingRounds,
      finalistsCount: config.finalistsCount,
    });
    
    this.state = {
      gameId: generateId(),
      config: { ...DEFAULT_GAME_CONFIG, ...config },
      phase: 'lobby',
      players: [],
      currentRound: null,
      roundHistory: [],
      cardPool: [],
      unlockedCardIds: [],
      finalists: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
  }

  addPlayer(player: PlayerState): boolean {
    if (this.state.players.length >= this.state.config.maxPlayers) return false;
    if (this.state.phase !== 'lobby') return false;

    // 敗退フラグ初期化
    player.isEliminated = false;
    player.eliminatedRound = undefined;

    this.state.players.push(player);
    this.readyStates.set(player.info.id, {
      playerId: player.info.id,
      isReady: player.info.type === 'bot', // Botは常に準備完了
      timestamp: Date.now(),
    });
    
    this.emit({ type: 'playerJoined', timestamp: Date.now(), data: player.info });
    return true;
  }

  removePlayer(playerId: string): boolean {
    const idx = this.state.players.findIndex((p) => p.info.id === playerId);
    if (idx === -1) return false;

    this.state.players.splice(idx, 1);
    this.readyStates.delete(playerId);
    
    this.emit({ type: 'playerLeft', timestamp: Date.now(), data: { playerId } });
    return true;
  }

  fillWithBots(): void {
    const needed = this.getNeededPlayerCount();
    for (let i = 0; i < needed; i++) {
      const bot = createBotPlayer(this.state.players.length);
      this.state.players.push(bot);
      
      // Botは常に準備完了
      this.readyStates.set(bot.info.id, {
        playerId: bot.info.id,
        isReady: true,
        timestamp: Date.now(),
      });
      
      const ai = new BotAI(bot.info.botDifficulty);
      this.botAIs.set(bot.info.id, ai);
    }
  }

  // サーバーから受信したBot情報でBotを追加 (非ホスト用)
  addBotFromSync(id: string, name: string, difficulty: string): void {
    // 既に存在する場合はスキップ
    if (this.state.players.some(p => p.info.id === id)) {
      return;
    }

    const bot: PlayerState = {
      info: {
        id,
        name,
        type: 'bot',
        botDifficulty: difficulty as 'easy' | 'normal' | 'hard',
      },
      deck: this.cardPool.generateStarterDeck(),
      bench: [],
      benchLimit: DEFAULT_BENCH_LIMIT,
      fans: 0,
      matchHistory: [],
      isEliminated: false,
    };

    this.state.players.push(bot);
    
    this.readyStates.set(id, {
      playerId: id,
      isReady: true,
      timestamp: Date.now(),
    });

    const ai = new BotAI(bot.info.botDifficulty);
    this.botAIs.set(id, ai);

    console.log('[GM] Added bot from sync:', name, id);
  }

  private getNeededPlayerCount(): number {
    const current = this.state.players.length;
    if (current === 0) return 2;
    if (current === 1) return 1;
    return current % 2 === 0 ? 0 : 1;
  }

  // プレイヤーの準備完了を設定
  setPlayerReady(playerId: string, isReady: boolean = true): void {
    const state = this.readyStates.get(playerId);
    if (!state) return;

    state.isReady = isReady;
    state.timestamp = Date.now();

    this.emit({
      type: 'playerReady' as any,
      timestamp: Date.now(),
      data: { playerId, isReady },
    });

    // 全員準備完了チェック
    this.checkAllReady();
  }

  // 準備完了状態取得
  getReadyStates(): { playerId: string; name: string; isReady: boolean; isBot: boolean }[] {
    return this.state.players.map((p) => ({
      playerId: p.info.id,
      name: p.info.name,
      isReady: this.readyStates.get(p.info.id)?.isReady ?? false,
      isBot: p.info.type === 'bot',
    }));
  }

  // 全員準備完了かチェック
  private checkAllReady(): boolean {
    const allReady = this.state.players.every((p) => {
      const state = this.readyStates.get(p.info.id);
      return state?.isReady === true;
    });

    if (allReady) {
      this.emit({
        type: 'allPlayersReady' as any,
        timestamp: Date.now(),
        data: {},
      });
    }

    return allReady;
  }

  // 全員準備完了待ち (Promise版)
  waitForAllReady(): Promise<void> {
    return new Promise((resolve, reject) => {
      // 既に全員準備完了
      if (this.checkAllReady()) {
        resolve();
        return;
      }

      // タイムアウト設定
      const timeout = setTimeout(() => {
        this.forceReadyTimeout();
        resolve();
      }, this.READY_TIMEOUT_MS);

      // 準備完了イベント監視
      const checkReady = (event: GameEvent) => {
        if ((event.type as string) === 'allPlayersReady') {
          clearTimeout(timeout);
          resolve();
        }
      };

      this.on(checkReady);
    });
  }

  // タイムアウト時の強制処理
  private forceReadyTimeout(): void {
    // 未準備のHumanプレイヤーをBotに置き換えるか、強制準備完了
    for (const player of this.state.players) {
      const state = this.readyStates.get(player.info.id);
      if (state && !state.isReady) {
        // 強制的に準備完了にする
        state.isReady = true;
        this.emit({
          type: 'playerForceReady' as any,
          timestamp: Date.now(),
          data: { playerId: player.info.id },
        });
      }
    }
  }

  // 準備完了状態リセット (新ラウンド開始時)
  private resetReadyStates(): void {
    for (const player of this.state.players) {
      const state = this.readyStates.get(player.info.id);
      if (state) {
        state.isReady = player.info.type === 'bot';
        state.timestamp = Date.now();
      }
    }
  }

  // 準備完了状態リセット (公開版 - マルチプレイ用)
  resetAllReadyStates(): void {
    this.resetReadyStates();
  }

  startGame(skipBotFill: boolean = false): boolean {
    if (this.state.phase !== 'lobby') return false;
    console.log('[GM] startGame: players=', this.state.players.length, 'fillWithBots=', this.state.config.fillWithBots, 'skipBotFill=', skipBotFill);
    
    // マルチプレイ時はBot追加をスキップ (ホストがマッチフェーズ開始時に行う)
    if (!skipBotFill) {
      if (this.state.players.length < 2) {
        if (this.state.config.fillWithBots) {
          this.fillWithBots();
        } else {
          return false;
        }
      }

      if (this.state.players.length % 2 !== 0 && this.state.config.fillWithBots) {
        console.log('[GM] Adding bot for odd player count');
        this.fillWithBots();
      }
    }
    console.log('[GM] After fillWithBots check: players=', this.state.players.length);

    this.cardPool.updateRound(1);
    this.state.unlockedCardIds = this.cardPool.getUnlockedCards().map((c) => c.id);

    // Botには自動でスターターデッキを設定
    for (const player of this.state.players) {
      if (player.info.type === 'bot') {
        player.deck = this.cardPool.generateStarterDeck();
        console.log('[GM] Bot deck set:', player.info.name, 'cards:', player.deck.length);
      }
      player.benchLimit = DEFAULT_BENCH_LIMIT;
    }

    // スターターデッキ選択フェーズへ
    this.state.phase = 'starterSelect';
    this.emit({ type: 'gameStarted', timestamp: Date.now(), data: { playerCount: this.state.players.length } });

    return true;
  }

  // スターターデッキ選択完了
  setStarterDeck(playerId: string, deck: { defId: string; instanceId: string; currentPower: number; isRevealed: boolean }[]): void {
    const player = this.state.players.find((p) => p.info.id === playerId);
    if (!player) return;

    player.deck = deck;
    this.setPlayerReady(playerId, true);
    
    this.emit({
      type: 'starterDeckSelected' as any,
      timestamp: Date.now(),
      data: { playerId, deckSize: deck.length },
    });
  }

  // スターターデッキ選択フェーズ完了 → ラウンド1開始
  completeStarterSelection(): void {
    if (this.state.phase !== 'starterSelect') return;

    // 未選択のHumanプレイヤーにはランダムデッキを設定
    for (const player of this.state.players) {
      if (player.deck.length === 0) {
        player.deck = this.cardPool.generateStarterDeck();
      }
    }

    this.resetReadyStates();
    this.startRound(1);
  }

  // CardPool取得 (UI用)
  getCardPool(): CardPool {
    return this.cardPool;
  }

  private startRound(roundNumber: number): void {
    const newCards = this.cardPool.updateRound(roundNumber);
    this.state.unlockedCardIds = this.cardPool.getUnlockedCards().map((c) => c.id);

    this.state.currentRound = {
      roundNumber,
      phase: 'deck',
      matches: [],
      matchResults: [],
    };

    // 準備状態リセット
    this.resetReadyStates();

    this.state.phase = 'deckPhase';
    this.emit({ type: 'roundStarted', timestamp: Date.now(), data: { round: roundNumber, newCards } });
  }

  // デッキフェイズ完了 (個別プレイヤー)
  playerCompleteDeckPhase(playerId: string): void {
    this.setPlayerReady(playerId, true);
  }

  // 全員準備完了後にマッチフェイズへ
  completeDeckPhase(): void {
    if (this.state.phase !== 'deckPhase' || !this.state.currentRound) return;

    const deckPhase = new DeckPhase(this.cardPool, this.state.currentRound.roundNumber, this.state.config.tierDraws);
    for (const player of this.state.players) {
      if (player.info.type === 'bot') {
        const ai = this.botAIs.get(player.info.id);
        if (ai) {
          ai.processDeckPhase(player, deckPhase);
        }
      }
    }

    this.state.currentRound.phase = 'match';
    this.state.phase = 'matchPhase';
    this.emit({ type: 'phaseChanged', timestamp: Date.now(), data: { phase: 'matchPhase' } });

    this.runMatches();
  }

  // 全員準備完了を待ってからマッチフェイズへ (非同期版)
  async completeDeckPhaseWhenReady(): Promise<void> {
    if (this.state.phase !== 'deckPhase' || !this.state.currentRound) return;

    // Botのデッキフェイズ処理
    const deckPhase = new DeckPhase(this.cardPool, this.state.currentRound.roundNumber, this.state.config.tierDraws);
    for (const player of this.state.players) {
      if (player.info.type === 'bot') {
        const ai = this.botAIs.get(player.info.id);
        if (ai) {
          ai.processDeckPhase(player, deckPhase);
        }
        // Botは自動で準備完了
        this.setPlayerReady(player.info.id, true);
      }
    }

    // 全員準備完了待ち
    await this.waitForAllReady();

    // マッチフェイズへ
    this.state.currentRound.phase = 'match';
    this.state.phase = 'matchPhase';
    this.emit({ type: 'phaseChanged', timestamp: Date.now(), data: { phase: 'matchPhase' } });

    // UIがバトルを制御するため、ここでは自動実行しない
    // runMatchesはcompleteMatchesから呼ばれる
  }

  // マッチのペアリング情報を取得 (UI用)
  getMatchPairings(): { player1: PlayerState; player2: PlayerState; firstPlayerId: string }[] {
    if (!this.state.currentRound) return [];
    
    const roundNum = this.state.currentRound.roundNumber;
    const pairings = this.tournament.generatePairings(this.state.players, roundNum);
    
    return pairings.map((pairing) => {
      const p1 = this.state.players.find((p) => p.info.id === pairing.player1Id)!;
      const p2 = this.state.players.find((p) => p.info.id === pairing.player2Id)!;
      
      const p1Trophies = p1.matchHistory.filter((m) => m.result === 'win').map((m) => m.round);
      const p2Trophies = p2.matchHistory.filter((m) => m.result === 'win').map((m) => m.round);
      
      const firstPlayerId = determineFirstPlayer(p1.info.id, p1Trophies, p2.info.id, p2Trophies);
      
      return { player1: p1, player2: p2, firstPlayerId };
    });
  }

  // マッチフェイズを作成 (UI用)
  createMatchPhase(player1Id: string, player2Id: string, firstPlayerId: string): MatchPhase | null {
    if (!this.state.currentRound) return null;
    
    const p1 = this.state.players.find((p) => p.info.id === player1Id);
    const p2 = this.state.players.find((p) => p.info.id === player2Id);
    
    if (!p1 || !p2) return null;
    
    return new MatchPhase(
      this.state.currentRound.roundNumber,
      p1.info.id,
      [...p1.deck],
      p2.info.id,
      [...p2.deck],
      firstPlayerId
    );
  }

  // マッチ結果を登録 (UI用)
  registerMatchResult(result: MatchResult): void {
    if (!this.state.currentRound) return;
    
    const roundNum = this.state.currentRound.roundNumber;
    this.state.currentRound.matchResults.push(result);

    // 勝者のファン獲得
    const winner = this.state.players.find((p) => p.info.id === result.winnerId);
    if (winner) {
      const fansGained = this.tournament.calculateFansGained(roundNum, result.winnerFlags);
      winner.fans += fansGained;
      winner.matchHistory.push({
        round: roundNum,
        opponentId: result.loserId,
        result: 'win',
        fansGained,
      });
    }

    // 敗者のファン減少と敗退チェック
    const loser = this.state.players.find((p) => p.info.id === result.loserId);
    if (loser) {
      const fansLost = this.tournament.calculateFansLost(roundNum);
      loser.fans = Math.max(0, loser.fans - fansLost);
      loser.matchHistory.push({
        round: roundNum,
        opponentId: result.winnerId,
        result: 'lose',
        fansGained: -fansLost,
      });

      // 敗退チェック
      const losses = loser.matchHistory.filter((m) => m.result === 'lose').length;
      if (losses >= 3) {
        loser.isEliminated = true;
        loser.eliminatedRound = roundNum;
        this.emit({
          type: 'playerEliminated' as any,
          timestamp: Date.now(),
          data: { playerId: loser.info.id, round: roundNum },
        });
      }
    }

    this.emit({ type: 'matchEnded', timestamp: Date.now(), data: result });
  }

  // 全マッチ完了後にラウンド終了 (UI用)
  completeMatches(): void {
    this.endRound();
  }

  // Bot同士のマッチを自動実行
  private runBotMatches(): void {
    if (!this.state.currentRound) return;

    const roundNum = this.state.currentRound.roundNumber;
    const pairings = this.tournament.generatePairings(this.state.players, roundNum);

    for (const pairing of pairings) {
      const p1 = this.state.players.find((p) => p.info.id === pairing.player1Id);
      const p2 = this.state.players.find((p) => p.info.id === pairing.player2Id);

      if (!p1 || !p2) continue;

      // 両方Botの場合のみ自動実行
      if (p1.info.type === 'bot' && p2.info.type === 'bot') {
        const p1Trophies = p1.matchHistory.filter((m) => m.result === 'win').map((m) => m.round);
        const p2Trophies = p2.matchHistory.filter((m) => m.result === 'win').map((m) => m.round);
        const firstPlayerId = determineFirstPlayer(p1.info.id, p1Trophies, p2.info.id, p2Trophies);

        const match = new MatchPhase(
          roundNum,
          p1.info.id,
          [...p1.deck],
          p2.info.id,
          [...p2.deck],
          firstPlayerId
        );

        const result = match.runToCompletion();
        if (result) {
          this.registerMatchResult(result);
        }
      }
    }
  }

  private runMatches(): void {
    // 互換性のため残す (Botマッチ自動実行用)
    this.runBotMatches();
  }

  private endRound(): void {
    if (!this.state.currentRound) return;

    this.state.roundHistory.push(this.state.currentRound);
    const roundNum = this.state.currentRound.roundNumber;

    // アクティブプレイヤー数チェック
    const activeCount = this.tournament.getActivePlayerCount(this.state.players);

    this.emit({ 
      type: 'roundEnded', 
      timestamp: Date.now(), 
      data: { 
        round: roundNum,
        activePlayers: activeCount,
        ranking: this.tournament.generateRanking(this.state.players),
      } 
    });

    // 予選終了条件: 規定ラウンド数達成、またはアクティブ1人以下(全員脱落防止)
    // 2人でも予選は実施する
    if (roundNum >= this.state.config.qualifyingRounds || activeCount <= 1) {
      this.startFinals();
    } else {
      // 次ラウンドへの遷移はUIが制御するため、phaseを変更するのみ
      this.state.phase = 'roundEnd';
    }
  }

  // 次ラウンド開始 (UIから呼ばれる)
  proceedToNextRound(): void {
    if (this.state.phase !== 'roundEnd') return;
    
    const lastRound = this.state.roundHistory[this.state.roundHistory.length - 1];
    if (lastRound) {
      this.startRound(lastRound.roundNumber + 1);
    }
  }

  private startFinals(): void {
    // 決勝進出者決定 (TournamentManagerを使用)
    const finalists = this.tournament.determinateFinalists(this.state.players);
    this.state.finalists = finalists.map((p) => p.info.id);

    this.state.phase = 'finals';
    this.emit({ 
      type: 'finalsStarted', 
      timestamp: Date.now(), 
      data: { 
        finalists: this.state.finalists,
        finalistDetails: finalists.map((p) => ({
          id: p.info.id,
          name: p.info.name,
          fans: p.fans,
          wins: p.matchHistory.filter((m) => m.result === 'win').length,
        })),
      } 
    });

    // 決勝戦はrunFinalsMatchで実行 (UIトリガー)
  }

  // 決勝戦実行 (UIから呼ばれる)
  runFinalsMatch(): void {
    if (this.state.phase !== 'finals') return;
    if (this.state.finalists.length < 2) return;

    const p1 = this.state.players.find((p) => p.info.id === this.state.finalists[0]);
    const p2 = this.state.players.find((p) => p.info.id === this.state.finalists[1]);

    if (p1 && p2) {
      // 決勝戦の先行: トロフィー(勝利ラウンド番号)の最大値で比較
      const p1Trophies = p1.matchHistory
        .filter((m) => m.result === 'win')
        .map((m) => m.round);
      const p2Trophies = p2.matchHistory
        .filter((m) => m.result === 'win')
        .map((m) => m.round);
      
      const firstPlayerId = determineFirstPlayer(
        p1.info.id, p1Trophies,
        p2.info.id, p2Trophies
      );

      const finals = new MatchPhase(
        8, // Finals round
        p1.info.id,
        [...p1.deck],
        p2.info.id,
        [...p2.deck],
        firstPlayerId
      );

      const result = finals.runToCompletion();
      if (result) {
        this.state.champion = result.winnerId;
        this.emit({ type: 'matchEnded', timestamp: Date.now(), data: result });
        this.endGame();
      }
    }
  }

  private endGame(): void {
    this.state.phase = 'gameEnd';
    
    const ranking = this.tournament.generateRanking(this.state.players);
    const champion = this.state.players.find((p) => p.info.id === this.state.champion);
    
    this.emit({
      type: 'gameEnded',
      timestamp: Date.now(),
      data: {
        champion: champion?.info,
        ranking,
      },
    });
  }

  private emit(event: GameEvent): void {
    this.state.updatedAt = Date.now();
    for (const listener of this.eventListeners) {
      listener(event);
    }
  }

  on(listener: (event: GameEvent) => void): void {
    this.eventListeners.push(listener);
  }

  getState(): GameState {
    return this.state;
  }

  // 設定を更新
  setConfig(config: Partial<GameConfig>): void {
    if (this.state.phase !== 'lobby') return;
    
    this.state.config = { ...this.state.config, ...config };
    
    // TournamentManagerも更新
    this.tournament = new TournamentManager({
      qualifyingRounds: this.state.config.qualifyingRounds,
      finalistsCount: this.state.config.finalistsCount,
    });
  }

  getPlayer(playerId: string): PlayerState | undefined {
    return this.state.players.find((p) => p.info.id === playerId);
  }

  getRanking(): { rank: number; player: PlayerState; status: string }[] {
    return this.tournament.generateRanking(this.state.players);
  }

  // TournamentManager取得
  getTournament(): TournamentManager {
    return this.tournament;
  }
}
